import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Button,
  Modal,
  IconButton,
  Menu,
  MenuItem,
  TextField
} from '@mui/material';
import * as XLSX from 'xlsx';
import { listInsurances, deleteInsurance, setReminder } from '../../api';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DownloadIcon from '@mui/icons-material/Download';

const formatDate = (date) => {
  if (!date || isNaN(new Date(date))) return 'N/A';
  return new Date(date).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const InsuranceTable = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [insurances, setInsurances] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const rowRefs = useRef({});

  const fetchInsurances = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listInsurances();
      console.log('API Response:', data); // Debug log
      if (Array.isArray(data)) {
        setInsurances(data);
      } else if (data && typeof data === 'object' && 'data' in data) {
        // Handle case where response.data is nested (e.g., { data: [...], meta: {} })
        setInsurances(Array.isArray(data.data) ? data.data : []);
      } else {
        setInsurances([]);
        setError('Invalid data format received from API');
      }
    } catch (err) {
      setError(`Failed to fetch insurances: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInsurances();
  }, []);

  useEffect(() => {
    if (id && rowRefs.current[id]) {
      rowRefs.current[id].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [id, insurances]);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this insurance?')) {
      try {
        await deleteInsurance(id);
        fetchInsurances();
      } catch (err) {
        setError(`Delete failed: ${err.message}`);
      }
    }
  };

  const handleView = (row) => {
    setViewData(row || {});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setViewData(null);
  };

  const handleReminderClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedRecordId(id);
  };

  const handleReminderClose = () => {
    setAnchorEl(null);
    setSelectedRecordId(null);
  };

  const handleSetReminder = async (days) => {
    try {
      await setReminder(selectedRecordId, 'insurance', days);
      handleReminderClose();
      fetchInsurances();
      alert(`Reminder set for ${days} day(s) before policy end`);
    } catch (err) {
      setError(`Reminder set failed: ${err.message}`);
    }
  };

  const handleExport = () => {
    const exportData = insurances.map(item => ({
      'Insurance Company': item.insurance_company || 'N/A',
      'Policy Number': item.policy_no || 'N/A',
      'Total Policy Value': item.total_policy_value || 'N/A',
      'Covered Locations': item.covered_locations?.map(loc => loc.covered_location || 'N/A').join(', ') || 'N/A',
      'Policy Date': formatDate(item.policy_date),
      'Agent Name': item.agent_name || 'N/A',
      'Agent Contact': item.agent_contact_no || 'N/A',
      'Policy Type': item.policy_type || 'N/A',
      'Occupancy': item.occupancy || 'N/A'
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Insurances');
    XLSX.writeFile(workbook, 'insurance_policies.xlsx');
  };

  const filtered = insurances.filter((item) => {
    const search = searchTerm.toLowerCase();
    return (
      item.insurance_company?.toLowerCase().includes(search) ||
      item.policy_no?.toLowerCase().includes(search) ||
      formatDate(item.policy_date).includes(search)
    );
  });

  if (loading) return <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>;
  if (error) {
    return (
      <Box display="flex" justifyContent="center" p={3} alignItems="center">
        <Typography color="error" mr={2}>{error}</Typography>
        <Button variant="contained" onClick={fetchInsurances} sx={{ backgroundColor: '#e0b256' }}>Retry</Button>
      </Box>
    );
  }

  return (
    <>
      <Paper elevation={0} sx={{ p: 2, backgroundColor: '#121212' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Insurance Policies</Typography>
          <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleExport} sx={{ backgroundColor: '#e0b256' }}>
            Export to Excel
          </Button>
        </Box>

        <TextField
          placeholder="Search by company, policy number, or date"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          sx={{ mb: 2, input: { color: '#fff' } }}
          InputProps={{ style: { backgroundColor: '#1e1e1e', borderRadius: 4, color: '#fff' } }}
        />

        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#ccc' }}>Company</TableCell>
              <TableCell sx={{ color: '#ccc' }}>Policy No</TableCell>
              <TableCell sx={{ color: '#ccc' }}>Total Policy Value</TableCell>
              <TableCell sx={{ color: '#ccc' }}>Covered Locations</TableCell>
              <TableCell sx={{ color: '#ccc' }}>Policy Date</TableCell>
              <TableCell sx={{ color: '#ccc' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length > 0 ? (
              filtered.map((row) => (
                <TableRow key={row._id} ref={(el) => (rowRefs.current[row._id] = el)} sx={{ '&:hover': { backgroundColor: '#333' } }}>
                  <TableCell sx={{ color: '#fff' }}>{row.insurance_company || 'N/A'}</TableCell>
                  <TableCell sx={{ color: '#fff' }}>{row.policy_no || 'N/A'}</TableCell>
                  <TableCell sx={{ color: '#fff' }}>{row.total_policy_value || 'N/A'}</TableCell>
                  <TableCell sx={{ color: '#fff' }}>
                    {row.covered_locations?.map(loc => loc.covered_location || 'N/A').join(', ') || 'N/A'}
                  </TableCell>
                  <TableCell sx={{ color: '#fff' }}>{formatDate(row.policy_date)}</TableCell>
                  <TableCell>
                    <IconButton aria-label="View insurance details" onClick={() => handleView(row)} sx={{ color: '#e0b256' }}>
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton aria-label="Edit insurance" onClick={() => navigate(`/edit-insurance/${row._id}`, { state: { data: row } })} sx={{ color: '#e0b256' }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton aria-label="Delete insurance" onClick={() => handleDelete(row._id)} sx={{ color: '#e0b256' }}>
                      <DeleteIcon />
                    </IconButton>
                    <IconButton aria-label="Set reminder" onClick={(e) => handleReminderClick(e, row._id)} sx={{ color: '#e0b256' }}>
                      <NotificationsIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow><TableCell colSpan={6} sx={{ color: '#ccc' }}>No insurance policies found</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

    <Modal open={openModal} onClose={handleCloseModal}>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      bgcolor: '#121212',
      p: 4,
      borderRadius: 2,
      minWidth: 400,
      maxWidth: 600,
      color: '#fff',
    }}
  >
    <Typography variant="h6" gutterBottom>Insurance Details</Typography>

    {viewData &&
      Object.entries(viewData)
        .filter(([key]) => !['_id', '__v', 'created_by', 'created_at'].includes(key))
        .map(([key, val]) => (
          <Typography key={key} variant="body2" sx={{ mb: 1.5 }}>
            <strong>{key.replace(/_/g, ' ').toUpperCase()}:</strong>

            {key === 'risk_covers' && Array.isArray(val) ? (
              val.length > 0 ? (
                <ul style={{ paddingLeft: 20 }}>
                  {val.map((rc, idx) => (
                    <li key={idx}>
                      <strong>{rc.risk_cover || 'N/A'}:</strong> ₹{rc.policy_value || 0}{' '}
                      (Sum Assured: ₹{rc.sum_assured || 0}, Rate: {rc.rate || 0}%, GST: {rc.gst_value || 0}%, Requirement: ₹{rc.current_requirement || 0}, Diff: ₹{rc.difference || 0})
                    </li>
                  ))}
                </ul>
              ) : (
                'N/A'
              )
            ) : Array.isArray(val) ? (
              val.length > 0 ? (
                <ul style={{ paddingLeft: 20 }}>
                  {val.map((item, index) => (
                    <li key={index}>
                      {item.covered_location || JSON.stringify(item) || 'N/A'}
                    </li>
                  ))}
                </ul>
              ) : (
                'N/A'
              )
            ) : typeof val === 'object' && val !== null ? (
              Object.keys(val).length > 0 ? (
                <div>
                  {Object.entries(val).map(([subKey, subVal]) => (
                    <Typography key={subKey} variant="body2" sx={{ ml: 2 }}>
                      {subKey.replace(/_/g, ' ').toUpperCase()}: {subVal || 'N/A'}
                    </Typography>
                  ))}
                </div>
              ) : (
                'N/A'
              )
            ) : (
              <span> {val || 'N/A'}</span>
            )}
          </Typography>
        ))}

    <Button fullWidth variant="contained" onClick={handleCloseModal} sx={{ mt: 2, backgroundColor: '#e0b256' }}>
      Close
    </Button>
  </Box>
</Modal>


      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleReminderClose} PaperProps={{ sx: { bgcolor: '#121212', color: '#fff', p: 2, width: 220 } }}>
        {[30, 15, 5, 1].map((days) => (
          <MenuItem key={days} onClick={() => handleSetReminder(days)} sx={{ '&:hover': { bgcolor: '#e0b256', color: '#000' } }}>
            {days} Day{days > 1 ? 's' : ''} Before
          </MenuItem>
        ))}
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>Custom Days</Typography>
          <input
            type="number"
            min="1"
            placeholder="Enter days"
            style={{ width: '100%', padding: '6px', borderRadius: '4px', border: '1px solid #999', backgroundColor: '#1f1f1f', color: '#fff' }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const days = parseInt(e.target.value);
                if (!isNaN(days) && days > 0) handleSetReminder(days);
                else alert('Please enter a valid number of days');
              }
            }}
          />
        </Box>
      </Menu>
    </>
  );
};

export default InsuranceTable;